/**
 * The ChiCare icon set.
 *
 * Ported one-for-one from the `<symbol>` sprite in ChiCare-Mockups/. Every path
 * is drawn on a 24×24 grid with a 1.7–2px stroke and no fill, which is what
 * makes them sit at the same optical weight as Satoshi body text.
 *
 * They are React components rather than an SVG sprite sheet for two reasons:
 * a sprite needs a same-document `<use href="#id">` that breaks the moment a
 * component renders inside a portal or a Storybook iframe, and React Native has
 * no sprite concept at all — this shape ports to `react-native-svg` directly.
 */

import type { SVGProps } from 'react';
import { cn } from '@/lib/cn';
import { iconPaths, type IconName } from './iconPaths';

export type { IconName };


export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  /** Pixel size. Defaults to 1em so an icon tracks the text it sits beside. */
  size?: number | string;
  /** Solid rather than outlined. The shield is filled when it means "verified". */
  filled?: boolean;
  strokeWidth?: number;
  /** Give the icon an accessible name; omit for purely decorative icons. */
  title?: string;
}

export function Icon({
  name,
  size = '1em',
  filled = false,
  strokeWidth = 1.8,
  title,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill={filled ? 'currentColor' : 'none'}
      stroke={filled ? 'none' : 'currentColor'}
      strokeWidth={filled ? undefined : strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('shrink-0', className)}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      {...props}
    >
      {title ? <title>{title}</title> : null}
      {iconPaths[name]}
    </svg>
  );
}
